/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';

import { Button, Dialog, Divider, Hidden, breakpoints } from '@coursera/cds-core';

import { useTracker } from 'bundles/page/lib/event-pulse/react';
import SortByIndex from 'bundles/search-common/components/SortByIndex';
import CheckableExpandableSearchFilterGroup, {
  type UnappliedFiltersByFacet,
} from 'bundles/search-common/components/experiments/filters2024/CheckableExpandableSearchFilterGroup';
import { filterValueAdapter } from 'bundles/search-common/components/experiments/filters2024/filterValueAdapter';
import { getFiltersByFacet } from 'bundles/search-common/components/experiments/filters2024/utils';
import useSearchFilterConfigs from 'bundles/search-common/components/filters/useSearchFilterConfigs';
import { DEFAULT_SORTED_VALUE, SEARCH_FILTERS_INDEX_ID } from 'bundles/search-common/constants';
import { IS_PART_OF_COURSERA_PLUS } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchPageEventingData, SearchSortType } from 'bundles/search-common/types';
import useStableSearchResults from 'bundles/search-common/utils/useStableSearchResults';
import { createFilterKeyToOptionsMap, scrollUpIfNecessary } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  eventingData: SearchPageEventingData;
};

const styles = {
  modalContainer: css`
    .cds-Dialog-dialog {
      max-width: 864px;
    }
  `,
  divider: (isPartOfCourseraPlus: boolean) => css`
    margin: var(--cds-spacing-150) 0 var(--cds-spacing-300);

    &:first-of-type {
      margin-bottom: ${isPartOfCourseraPlus ? 'var(--cds-spacing-200)' : 'var(--cds-spacing-300)'};
    }
  `,
  secondaryButton: css`
    margin: 0 0 0 var(--cds-spacing-200);
    ${breakpoints.down('xs')} {
      margin: var(--cds-spacing-100) 0 0 0;
    }
  `,
};

const AllSearchFiltersModal = (props: Props) => {
  const { isOpen, onClose, eventingData } = props;
  const { HeadingGroup, Content, Actions } = Dialog;
  const applyButtonText = _t('Show results');
  const resetButtonText = _t('Reset');
  const { filterGroupConfigs, filtersListOrder } = useSearchFilterConfigs();
  const { setFiltersAndSortBy, sortBy } = useSearch();
  const searchData = useStableSearchResults();
  const { facets: allFilters } = useStableSearchResults(SEARCH_FILTERS_INDEX_ID) ?? {};
  const { facets, facetFilters } = searchData || {};
  const filterKeyToOptionsMap = useMemo(
    () => createFilterKeyToOptionsMap(facets, facetFilters, filtersListOrder, allFilters),
    [allFilters, facetFilters, facets, filtersListOrder]
  );
  const [unappliedFilters, setUnappliedFilters] = useState<UnappliedFiltersByFacet>({});
  const [selectedSortByValue, setSelectedSortByValue] = useState<SearchSortType>(sortBy);

  const track = useTracker();

  // This is to initially update the state with any filters that have already been selected
  useEffect(() => {
    if (facetFilters) {
      setUnappliedFilters(getFiltersByFacet(facetFilters));
    }
  }, [facetFilters]);

  // This is to update the state with any changes to the sort that happen outside of the modal
  useEffect(() => {
    if (sortBy) {
      setSelectedSortByValue(sortBy);
    }
  }, [sortBy]);

  const handleFilterChange = (newFilters: UnappliedFiltersByFacet) => {
    setUnappliedFilters((prevUnappliedFilters) => ({ ...prevUnappliedFilters, ...newFilters }));
  };

  const handleSortByChange = (sortValue: SearchSortType) => {
    setSelectedSortByValue(sortValue);
  };

  const handleApply = () => {
    setFiltersAndSortBy(selectedSortByValue, Object.values(unappliedFilters));
    onClose();
    scrollUpIfNecessary({ mustBeScrolledPastThisElementId: 'search-results-header-wrapper' });

    // prevent event from firing, when there are no filters (i.e is clicked after a reset)
    if (Object.keys(unappliedFilters).length) {
      track('apply_filter', {
        filter: Object.values(unappliedFilters).flat(),
        filterSource: 'unified_modal',
      });
    }
  };

  const handleReset = () => {
    track('apply_filter', {
      filter: [],
      filterSource: 'unified_modal',
      isReset: true,
    });

    setSelectedSortByValue(DEFAULT_SORTED_VALUE);
    setUnappliedFilters({});
  };

  const handleClose = () => {
    onClose();
    setUnappliedFilters(getFiltersByFacet(facetFilters));
    setSelectedSortByValue(sortBy);
  };

  return (
    <Dialog
      css={styles.modalContainer}
      width="large"
      sticky="actions"
      aria-labelledby="dialogHeading"
      open={isOpen}
      onClose={handleClose}
    >
      <HeadingGroup id="dialogHeading">{_t('Filter and sort')}</HeadingGroup>
      <Content>
        <SortByIndex changeIndex={handleSortByChange} sortByValue={selectedSortByValue} />
        {filterGroupConfigs
          .filter((group) => !!filterKeyToOptionsMap[group.property]?.length)
          .map((group) => (
            <div key={group.property}>
              <Divider css={styles.divider(group.property === IS_PART_OF_COURSERA_PLUS)} />
              <CheckableExpandableSearchFilterGroup
                groupConfig={group}
                eventingData={{
                  searchIndex: eventingData.searchIndex,
                  searchIndexPosition: eventingData.searchIndexPosition,
                }}
                {...filterValueAdapter(
                  group.property,
                  filterKeyToOptionsMap[group.property],
                  unappliedFilters[group.property],
                  handleFilterChange
                )}
              />
            </div>
          ))}
      </Content>
      <Actions>
        <Hidden xsDown>
          <Button variant="primary" onClick={handleApply}>
            {applyButtonText}
          </Button>
          <Button variant="secondary" css={styles.secondaryButton} onClick={handleReset}>
            {resetButtonText}
          </Button>
        </Hidden>
        <Hidden smUp>
          <Button fullWidth variant="primary" size="small" onClick={handleApply}>
            {applyButtonText}
          </Button>
          <Button fullWidth variant="ghost" size="small" css={styles.secondaryButton} onClick={handleReset}>
            {resetButtonText}
          </Button>
        </Hidden>
      </Actions>
    </Dialog>
  );
};

export default AllSearchFiltersModal;
